/**
 * Bounds of a Date section: `minDate` is `'today'`, a `YYYY-MM-DD` day or absent, `maxDate` a
 * day or absent. "today" stays a rule rather than a stored date because every brief keeps its
 * own copy of the template config, so it is resolved against the briefer's local clock at the
 * moment of use. Days are compared at local midnight, matching what the DatePicker hands back.
 */
// eslint-disable-next-line i18next/no-literal-string -- wire value: assets-api validates `minDate: 'today'` byte for byte
export const DATE_BOUND_TODAY = 'today';

const CALENDAR_DAY = /^(\d{4})-(\d{2})-(\d{2})$/;

export interface DateBounds {
  min: Date | null;
  max: Date | null;
}

export function parseCalendarDay(value: unknown): Date | null {
  if (typeof value !== 'string') return null;
  const match = CALENDAR_DAY.exec(value);
  if (!match) return null;
  const [year, month, day] = match.slice(1).map(Number) as [number, number, number];
  const date = new Date(year, month - 1, day);
  const isRealDay =
    date.getFullYear() === year && date.getMonth() === month - 1 && date.getDate() === day;
  return isRealDay ? date : null;
}

/** `toISOString` would write the UTC day, which is the previous one east of Greenwich. */
export function toCalendarDay(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

const startOfLocalDay = (date: Date) =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export function resolveDateBounds(
  config: Record<string, unknown>,
  now: Date = new Date(),
): DateBounds {
  return {
    min:
      config.minDate === DATE_BOUND_TODAY ? startOfLocalDay(now) : parseCalendarDay(config.minDate),
    max: parseCalendarDay(config.maxDate),
  };
}

export function isWithinDateBounds(date: Date, bounds: DateBounds): boolean {
  const day = startOfLocalDay(date).getTime();
  if (bounds.min && day < bounds.min.getTime()) return false;
  if (bounds.max && day > bounds.max.getTime()) return false;
  return true;
}

/**
 * Stored values may carry a trailing time, so each end is read by its leading calendar day. An
 * end whose day matches `stored` is skipped, as the server only checks an end that changed; a
 * "today" minimum would otherwise refuse every edit to a range whose start has passed.
 */
export function isDateValueWithinBounds(
  value: { start: string | null; end: string | null },
  config: Record<string, unknown>,
  stored?: unknown,
  now: Date = new Date(),
): boolean {
  const bounds = resolveDateBounds(config, now);
  const previous = (stored && typeof stored === 'object' ? stored : {}) as {
    start?: unknown;
    end?: unknown;
  };
  const dayOf = (end: unknown) => (typeof end === 'string' ? end.slice(0, 10) : null);
  const ends: [unknown, unknown][] = [
    [value.start, previous.start],
    [value.end, previous.end],
  ];
  return ends.every(([next, before]) => {
    const day = dayOf(next);
    if (day === null || day === dayOf(before)) return true;
    const parsed = parseCalendarDay(day);
    return parsed !== null && isWithinDateBounds(parsed, bounds);
  });
}
